// src/modules/app-views/leads/pages/Init/modals/DistributeLeadsModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Col, Descriptions, Flex, Row, Typography, Alert, Spin } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import SimpleUsers from 'components/SimpleComponents/SimpleUsers';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const BADGE_STYLE = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'inline-block',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const Step1 = ({ leads = [], boardOptions = [], loadingBoards = false, boardStatus = 'ok' }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const leadsCount = leads.length;
  const multipleLeadsText =
    leadsCount > 1
      ? `${translateX('distributing_leads_count').replace('%s', String(leadsCount))}`
      : null;

  // Descriptions items for the single-lead case
  const singleLeadItems = leadsCount === 1 ? buildLeadDescriptionItems(leads[0]) : null;

  // Determine alert message based on boardStatus
  const alertMessage =
    boardStatus === 'no_offers'
      ? translateX('no_offer_for_lp')
      : boardStatus === 'no_board_in_offers'
        ? translateX('no_board_for_offer_lp')
        : null;

  const showAlert = boardStatus !== 'ok' && !loadingBoards;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Row gutter={ROW_GUTTER} className="mt-3">
        <Col xs={24}>
          {singleLeadItems ? (
            <Descriptions column={1} size="small" items={singleLeadItems} bordered />
          ) : (
            <Text type="secondary">{multipleLeadsText}</Text>
          )}
        </Col>
      </Row>
      {showAlert && (
        <Row gutter={ROW_GUTTER} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Alert type="warning" title={alertMessage} showIcon />
          </Col>
        </Row>
      )}
      {!showAlert && (
        <Row gutter={ROW_GUTTER} style={{ marginTop: 16 }}>
          <Col xs={24}>
            {loadingBoards ? (
              <Spin size="small" />
            ) : (
              <SimpleSelect
                label="board"
                name="boardId"
                options={boardOptions}
                loading={loadingBoards}
                useCustomLabel
                optionRender={boardOptionRender}
                labelRender={props => boardLabelRender(props, boardOptions)}
                filterOption={boardFilterOption}
                disabled={boardStatus !== 'ok'}
                required
              />
            )}
          </Col>
        </Row>
      )}

      {!showAlert && (
        <Row gutter={ROW_GUTTER} style={{ marginTop: 0 }}>
          <Col xs={24}>
            <SimpleUsers label="consultant" name="consultantId" required />
          </Col>
        </Row>
      )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default Step1;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Builds Descriptions items for a single lead: name+interest row and landingPage+url row
function buildLeadDescriptionItems(lead) {
  return [
    {
      key: 'lead',
      label: 'Lead',
      children: (
        <div>
          <span style={{ fontWeight: 600 }} className="text-uppercase">
            {lead?.name || '—'}
          </span>
          {lead?.interest && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }} className="text-uppercase">
              {lead.interest}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'source',
      label: translateX('source'),
      children: (
        <div>
          <span style={{ fontWeight: 600 }} className="text-uppercase">
            {lead?.landingPageData?.name || '—'}
          </span>
          {lead?.leadMeta?.pageUrl && (
            <div
              style={{ fontSize: 12, color: '#888', marginTop: 2, wordBreak: 'break-all' }}
              className="text-lowercase"
            >
              {lead.leadMeta.pageUrl}
            </div>
          )}
        </div>
      ),
    },
  ];
}

// Renders each option in the dropdown: colored dot + board name
function boardOptionRender(option) {
  return (
    <Flex align="center" gap={8}>
      <span style={{ ...BADGE_STYLE, backgroundColor: option.data?.color || '#1890ff' }} />
      <span>{option.label}</span>
    </Flex>
  );
}

// Renders the selected value in the trigger: colored dot + board name
// AntD labelRender only receives { value, label } — look up color from options array
function boardLabelRender(props, options = []) {
  const option = options.find(o => o.value === props.value);
  const color = option?.color || '#1890ff';
  return (
    <Flex align="center" gap={8}>
      <span style={{ ...BADGE_STYLE, backgroundColor: color }} />
      <span>{props.label}</span>
    </Flex>
  );
}

// Custom filter since label is plain text (not JSX)
function boardFilterOption(input, option) {
  const label = typeof option?.label === 'string' ? option.label : '';
  return label.toLowerCase().includes((input || '').toLowerCase());
}
